(() => {
  const TURNSTILE_ENABLED = false;
  let turnstileLoader;

  function loadTurnstile() {
    if (!TURNSTILE_ENABLED) return Promise.resolve(null);
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (turnstileLoader) return turnstileLoader;
    turnstileLoader = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-freexar-turnstile]');
      const script = existing || document.createElement('script');
      const timeout = window.setTimeout(() => reject(new Error('Security verification timed out.')), 15000);
      script.addEventListener('load', () => {
        window.clearTimeout(timeout);
        if (window.turnstile) resolve(window.turnstile);
        else reject(new Error('Security verification did not load.'));
      }, { once: true });
      script.addEventListener('error', () => {
        window.clearTimeout(timeout);
        reject(new Error('Security verification could not be loaded.'));
      }, { once: true });
      if (!existing) {
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.freexarTurnstile = 'true';
        document.head.appendChild(script);
      }
    });
    return turnstileLoader;
  }

  function createSubmissionId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  class WarrantyForm {
    constructor(form) {
      this.form = form;
      this.endpoint = form.dataset.endpoint;
      this.siteKey = form.dataset.turnstileSiteKey;
      this.status = form.querySelector('[data-form-status]');
      this.submitButton = form.querySelector('[data-submit-button]');
      this.submitLabel = form.querySelector('[data-submit-label]');
      this.defaultLabel = this.submitLabel.textContent;
      this.submissionId = createSubmissionId();
      this.widgetId = null;
      form.addEventListener('input', (event) => this.clearError(event.target.name));
      form.addEventListener('submit', (event) => this.submit(event));
      this.initializeTurnstile();
    }

    async initializeTurnstile() {
      if (!TURNSTILE_ENABLED) return;
      if (!this.siteKey) return this.setStatus('Security verification is not configured.', 'error');
      try {
        const turnstile = await loadTurnstile();
        const container = document.createElement('div');
        container.className = 'freexar-warranty__turnstile';
        container.dataset.turnstileContainer = '';
        this.form.querySelector('.freexar-warranty__privacy')?.before(container);
        if (!document.documentElement.contains(container)) return;
        this.widgetId = turnstile.render(container, { sitekey: this.siteKey, action: 'warranty_registration' });
      } catch (error) {
        this.setStatus(error.message || 'Security verification could not be loaded.', 'error');
      }
    }

    field(name) {
      return this.form.elements[name];
    }

    setError(name, message) {
      const wrapper = this.form.querySelector(`[data-field="${CSS.escape(name)}"]`);
      const control = this.field(name);
      if (!wrapper || !control) return;
      wrapper.querySelector('[data-field-error]').textContent = message;
      if (message) control.setAttribute('aria-invalid', 'true');
      else control.removeAttribute('aria-invalid');
    }

    clearError(name) {
      if (name) this.setError(name, '');
    }

    validate() {
      let valid = true;
      for (const [name, message] of [
        ['name', 'Enter your name.'],
        ['email', 'Enter a valid email address.'],
        ['orderNumber', 'Enter your order number.']
      ]) {
        const control = this.field(name);
        const value = control.value.trim();
        let fieldValid = Boolean(value) && (name !== 'email' || control.validity.valid);
        let errorMessage = message;
        if (name === 'orderNumber' && value && !(/^[0-9]{3}-[0-9]{7}-[0-9]{7}$/.test(value) || value.startsWith('#FRX'))) {
          fieldValid = false;
          errorMessage = 'Enter an order number in the format 123-1234567-1234567 or starting with #FRX.';
        }
        this.setError(name, fieldValid ? '' : errorMessage);
        valid = fieldValid && valid;
      }
      if (!valid) this.form.querySelector('[aria-invalid="true"]')?.focus();
      return valid;
    }

    setStatus(message, state = '') {
      this.status.textContent = message;
      if (state) this.status.dataset.state = state;
      else delete this.status.dataset.state;
    }

    setSubmitting(submitting) {
      this.submitButton.disabled = submitting;
      this.submitLabel.textContent = submitting ? 'Submitting…' : this.defaultLabel;
    }

    resetTurnstile() {
      if (TURNSTILE_ENABLED && window.turnstile && this.widgetId !== null) window.turnstile.reset(this.widgetId);
    }

    async submit(event) {
      event.preventDefault();
      this.setStatus('');
      if (!this.validate()) return this.setStatus('Please correct the highlighted fields.', 'error');
      const token = TURNSTILE_ENABLED
        ? this.form.querySelector('[name="cf-turnstile-response"]')?.value || ''
        : '';
      if (TURNSTILE_ENABLED && !token) {
        return this.setStatus('Complete the security verification before submitting.', 'error');
      }

      const payload = {
        submissionId: this.submissionId,
        name: this.field('name').value.trim(),
        email: this.field('email').value.trim(),
        orderNumber: this.field('orderNumber').value.trim(),
        turnstileToken: token,
        companyWebsite: this.field('companyWebsite').value,
        pageUrl: window.location.href,
        locale: document.documentElement.lang || 'en'
      };
      this.setSubmitting(true);
      this.setStatus('Submitting your warranty application…');
      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.ok === false) throw new Error(data.error || 'The application could not be submitted.');
        const reference = data.applicationId || this.submissionId;
        this.form.reset();
        this.submissionId = createSubmissionId();
        this.resetTurnstile();
        this.setStatus(`Application received. We will review your order and email you if approved. Reference: ${reference}`, 'success');
      } catch (error) {
        this.setStatus(error.message || 'A network error interrupted the submission. Please try again.', 'error');
        this.resetTurnstile();
      } finally {
        this.setSubmitting(false);
      }
    }
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-warranty-form]:not([data-warranty-initialized])').forEach((form) => {
      form.dataset.warrantyInitialized = 'true';
      new WarrantyForm(form);
    });
  }

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
